import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';

import './lib/arrayExtensions';

import ClarityRouteTracker from './components/ClarityRouteTracker';
import { DebugEnvironmentPane, DebugSentryTestPane } from './components/debug';
import ErrorBoundary from './components/ErrorBoundary';
import MixpanelIdentifier from './components/MixpanelIdentifier';
import { useFeatureGate, useFeatureGates } from './hooks/useFeatureGates';
import {
  AccountOverviewPane,
  AccountPane,
  AssistantPane,
  DashboardPane,
  MerchantPane,
  ObligationsPane,
  RecurringTransactionsPane,
  SettingsPane,
  SpendingPane,
} from './panes';
import { PATHS } from './paths';
import AssistantSidebarProvider from './providers/AssistantSidebarProvider';
import AuthProvider from './providers/AuthProvider';
import MarkerProvider from './providers/MarkerProvider';
import TransactionsProvider from './providers/TransactionsProvider';
import { HomeScene, LoginScene, OnboardingScene } from './scenes';

const removeLeadingSlash = (path: string): string => {
  return path.startsWith('/') ? path.slice(1) : path;
};

/**
 * Wraps `ObligationsPane` with the `feat-obligations` feature-gate check.
 * Goes through `useFeatureGate` so the same `enabledFeatureGates` set
 * (server-side dev defaults included) that drives the sidebar entry
 * also gates the route. Renders `null` while the bootstrap query is
 * loading and no cached set exists yet, so a cache-cold tick can't
 * bounce a viewer the server is about to admit.
 */
const ObligationsRouteGate = () => {
  const enabled = useFeatureGate(FeatureGate.Obligations);
  const { flags, loading } = useFeatureGates();
  if (loading && flags.size === 0) {
    return null;
  }
  return enabled ? <ObligationsPane /> : <Navigate to={PATHS.DASHBOARD} replace />;
};

/**
 * Wraps `AssistantPane` with the `feat-assistant` feature-gate check.
 * Same fail-closed pattern as `ObligationsRouteGate` — hidden from the
 * sidebar by the same gate in `MainSidebar`, but the route guard is the
 * authority for direct URL access.
 *
 * At `xl+` the assistant lives as a side panel in `HomeScene` (open by
 * default), so this route would otherwise mount a *second* `AssistantPane`
 * and its live WebSocket — the server evicts the duplicate into a terminal
 * `superseded` state. We redirect to the dashboard there instead; the
 * already-mounted panel is the assistant. Below `xl` there is no room for a
 * side panel, so the route remains the assistant's home and renders the pane.
 * The redirect is keyed on the same `useBreakpoint('xl')` signal that gates
 * the panel's mount, keeping the two sites mutually exclusive.
 */
const AssistantRouteGate = () => {
  const enabled = useFeatureGate(FeatureGate.Assistant);
  const { flags, loading } = useFeatureGates();
  const isXl = useBreakpoint('xl');
  if (loading && flags.size === 0) {
    return null;
  }
  if (!enabled || isXl) {
    return <Navigate to={PATHS.DASHBOARD} replace />;
  }
  return <AssistantPane />;
};

/**
 * Parent-route guard for the `/debug/*` panes. Allows passage iff the
 * `debug-tools` feature gate is enabled for the viewer; otherwise
 * redirects to the dashboard. While the bootstrap query is in flight
 * with no cached set, renders `null` so a brief cache-cold tick can't
 * bounce a freshly-arriving admin off the page they came for. Uses
 * `<Outlet />` so a single guard covers every nested `/debug/*` route
 * — sister of `ObligationsRouteGate` above; both collapse into the
 * generic wrapper tracked in [ORN-767].
 */
const DebugRouteGate = () => {
  const enabled = useFeatureGate(FeatureGate.DebugTools);
  const { flags, loading } = useFeatureGates();
  if (loading && flags.size === 0) {
    return null;
  }
  return enabled ? <Outlet /> : <Navigate to={PATHS.DASHBOARD} replace />;
};

const MainApp = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <MarkerProvider>
            <ClarityRouteTracker />
            <MixpanelIdentifier />
            <TransactionsProvider>
              <AssistantSidebarProvider>
                <Routes>
                  <Route path={PATHS.HOME} element={<HomeScene />}>
                    <Route path={removeLeadingSlash(PATHS.ACCOUNTS)} element={<AccountOverviewPane />} />
                    <Route path={`${removeLeadingSlash(PATHS.ACCOUNTS)}/:accountId`} element={<AccountPane />} />
                    <Route path={`${removeLeadingSlash(PATHS.MERCHANTS)}/:merchantId`} element={<MerchantPane />} />
                    <Route element={<DebugRouteGate />}>
                      <Route path={removeLeadingSlash(PATHS.DEBUG_SENTRY_TEST)} element={<DebugSentryTestPane />} />
                      <Route path={removeLeadingSlash(PATHS.DEBUG_ENVIRONMENT)} element={<DebugEnvironmentPane />} />
                    </Route>
                    <Route path={removeLeadingSlash(PATHS.DASHBOARD)} element={<DashboardPane />} />
                    <Route path={removeLeadingSlash(PATHS.ASSISTANT)} element={<AssistantRouteGate />} />
                    <Route path={removeLeadingSlash(PATHS.SPENDING())} element={<SpendingPane />} />
                    <Route
                      path={removeLeadingSlash(PATHS.RECURRING_TRANSACTIONS)}
                      element={<RecurringTransactionsPane />}
                    />
                    <Route path={removeLeadingSlash(PATHS.OBLIGATIONS)} element={<ObligationsRouteGate />} />
                    <Route path={removeLeadingSlash(PATHS.SETTINGS)} element={<SettingsPane />} />
                    <Route index element={<Navigate to={PATHS.DASHBOARD} replace />} />
                  </Route>

                  <Route path={PATHS.LOGIN} element={<LoginScene />} />
                  <Route path={PATHS.ONBOARDING} element={<OnboardingScene />} />
                  <Route path="*" element={<Navigate to={PATHS.DASHBOARD} replace />} />
                </Routes>
              </AssistantSidebarProvider>
            </TransactionsProvider>
          </MarkerProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default MainApp;
